import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAllQuizQuestions } from "@/data/quiz";
import { useSearchCities } from "@/lib/api";
import ScreenHeading from "./ScreenHeading";
import OptionRow from "./OptionRow";
import {
  HEADING_GAP,
  LIST_GAP,
  ROW_BORDER,
  ROW_SHAPE,
  ROW_SURFACE,
} from "../layout";
import type { CityScreenDef, ScreenComponentProps } from "../types";

/**
 * "Where's home base?" — closes Act 1.
 *
 * This deliberately does NOT reuse the legacy `CitySelect`. That component
 * renders emoji tiles in rounded squares with its own padding and border
 * treatment, which lands in the middle of this flow looking like a different
 * product. Cities are just options, so they render as `OptionRow`s like every
 * other list here, and the search field borrows the same row geometry.
 *
 * `CitySelect` itself is untouched — the legacy quiz and stepper still use it,
 * including in light mode.
 *
 * Cities we haven't launched in are listed too, badged "Coming soon". They
 * are a real answer — someone in a city we don't serve yet should still be
 * able to finish onboarding and be told when we arrive, rather than being
 * forced to claim they live somewhere else. Picking one does NOT auto-advance:
 * the shared dock surfaces a Continue and an explanation, so the news doesn't
 * flash past on its way to the next screen.
 *
 * Picking a city we do serve advances immediately; the choice is what the
 * next handoff greets the user with.
 */

/** How many results to show before offering the rest. */
const VISIBLE_LIMIT = 5;

export default function CityScreen({
  def,
  rt,
}: ScreenComponentProps<CityScreenDef>) {
  const { t } = useT();
  const allQuestions = useAllQuizQuestions();
  const question = useMemo(
    () => allQuestions.find((q) => q.id === (def.questionId ?? "location")),
    [allQuestions, def.questionId],
  );

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  const selected = rt.answers[def.field];
  // No `activeOnly` — coming-soon cities belong in this list.
  const { data, isLoading } = useSearchCities(debounced, selected, 500);
  const cities = data?.success && data.data ? data.data : [];

  // A search collapses the "more cities" affordance — the list is already the
  // answer to what they typed.
  const searching = debounced.trim().length > 0;
  const showAll = expanded || searching;
  const visible = showAll ? cities : cities.slice(0, VISIBLE_LIMIT);
  const remaining = cities.length - visible.length;

  return (
    <div className="flex-1 flex flex-col">
      <ScreenHeading
        title={def.titleKey ? t(def.titleKey) : (question?.question ?? "")}
      />

      {/* Search field — same radius, padding and surface as a city row, so the
          two read as one control stack rather than a widget above a list. */}
      <div
        className={`${HEADING_GAP} flex items-center gap-3 ${ROW_BORDER} ${ROW_SHAPE} ${ROW_SURFACE} focus-within:border-primary-500 transition-colors`}
      >
        <Search className="w-[18px] h-[18px] flex-none text-neutral-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("quiz.citySelect.searchPlaceholder")}
          className="flex-1 min-w-0 bg-transparent border-0 p-0 text-[15px] leading-snug text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
        />
      </div>

      <div className={`mt-3 flex flex-col ${LIST_GAP}`}>
        {visible.map((city, i) => (
          <OptionRow
            key={city.id}
            index={i}
            label={city.name}
            badge={
              city.available ? undefined : t("quiz.citySelect.comingSoonBadge")
            }
            selected={selected === city.id}
            onClick={() => {
              rt.setAnswer(def.field, city.id);
              // Launched cities move straight on; a coming-soon pick waits for
              // the dock so its explanation is actually read.
              if (city.available) setTimeout(rt.next, 260);
            }}
          />
        ))}
      </div>

      {!isLoading && cities.length === 0 && (
        <p className="mt-6 text-center text-[13px] text-neutral-500 dark:text-neutral-400">
          {t("quiz.citySelect.noResults", { query })}
        </p>
      )}

      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-4 mb-2 self-center text-[13px] font-medium text-neutral-500 dark:text-neutral-400 hover:text-primary-400 transition-colors"
        >
          {t("onboarding.city.moreCities", { count: remaining })}
        </button>
      )}
    </div>
  );
}
